<template>
  <HeaderTitle>
    <template #top>{{ t("header.my") }} </template>
    <template #bottom>{{ t("feed.tab.label") }} </template>
  </HeaderTitle>
  <h2 class="text-display2 pb-4 pt-12">Highlights</h2>
  <ContentSlider :items-per-view="3">
    <div
      v-for="highlightArticle in highlightArticles"
      :key="highlightArticle.id"
    >
      <ArticleCard
        :key="highlightArticle.id"
        :article-id="highlightArticle.id"
        :title="highlightArticle.title"
        :image-url="
          highlightArticle.previewImage || highlightArticle.thumbnailURL
        "
        :image-alt-text="highlightArticle.previewImageAltText"
        :source-type="highlightArticle.sourceType"
        :teaser="highlightArticle.shortDescription"
        :is-favorite="highlightArticle.isFavorite"
      />
    </div>
  </ContentSlider>
  <div
    class="relative xl:max-w-screen-xl lg:max-w-screen-lg md:max-w-screen-md mx-auto mt-5 flex-auto"
  >
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-8 mt-8">
      <FeedTabBar v-model:current-tab="currentTab" />
      <div
        v-if="currentTab === FeedTabType.TAB_LIBRARY"
        class="justify-center w-full"
      >
        <SearchInput
          v-model="searchTerm"
          input-classes="input-primary w-full !border-0 !pl-12"
          size="sm"
          :placeholder="t('feed.search.placeholder')"
          @on-key-up-enter="searchArticles"
          @on-close-search="closeSearch"
        />
      </div>
    </div>

    <ArticleGrid
      v-if="currentTab === FeedTabType.TAB_DISCOVERY"
      :articles="sortedFeed"
    />
    <ArticleGrid
      v-else-if="
        searchTerm &&
        searchTerm.length >= 3 &&
        currentTab === FeedTabType.TAB_LIBRARY
      "
      :articles="searchedFeed"
    />
    <div
      v-if="
        searchTerm &&
        searchTerm.length >= 0 &&
        searchedFeed &&
        searchedFeed.length <= 0
      "
    >
      {{ t("articles.search.no.data") }}
    </div>
    <div
      v-if="
        currentTab === FeedTabType.TAB_LIBRARY &&
        (!searchTerm || (searchTerm && searchTerm.length <= 3))
      "
    >
      <HeadlineElement>{{
        t("feed.category.sections.for.me.title")
      }}</HeadlineElement>
      <ForYouGrid />
      <HeadlineElement>{{
        t("feed.category.sections.browse.title")
      }}</HeadlineElement>
      <CategoryGrid />
    </div>
  </div>
</template>

<script setup lang="ts">
import SearchInput from "../../../components/v2/layout/SearchInput.vue";
import ArticleGrid from "../../../components/v2/article/ArticleGrid.vue";
import ForYouGrid from "../../../components/v2/feed/ForYouGrid.vue";
import CategoryGrid from "../../../components/v2/feed/CategoryGrid.vue";
import HeadlineElement from "../../../components/v2/layout/HeadlineElement.vue";
import { useClientStore } from "../../../store/v2/client";
import { useUserStore } from "../../../store/v2/user";
import { fetchArticleFeed, fetchPromotionArticles } from "../../../api/v2/feed";
import FeedContentDTO from "../../../dto/feed-content.dto";
import HeaderTitle from "../../../components/v2/layout/header/HeaderTitle.vue";
import { useI18n } from "vue-i18n";
import FeedTabBar from "../../../components/v2/feed/FeedTabBar.vue";
import { FeedTabType } from "../../../enum/v2/feed-tab-type";
import { onMounted, ref, inject } from "vue";
import ContentSlider from "~/components/v2/layout/ContentSlider.vue";
import ArticleCard from "~/components/v2/article/ArticleCard.vue";

const { t } = useI18n();

const clientStore = useClientStore();
const userStore = useUserStore();

const sortFeedContent =
  inject<(feed: FeedContentDTO[]) => FeedContentDTO[]>("sortFeedContent");

const sortedFeed = ref<FeedContentDTO[] | null>(null);
const searchedFeed = ref(null);
const highlightArticles = ref(null);
const searchTerm = ref("");
let initialTab = FeedTabType.TAB_DISCOVERY;
if (window.history.state && window.history.state.fromFavorite) {
  initialTab = FeedTabType.TAB_LIBRARY;
}
const currentTab = ref<FeedTabType>(initialTab);

onMounted(() => {
  getArticleFeed();
  getHighlightArticles();
});

async function getArticleFeed() {
  const res = await fetchArticleFeed(
    clientStore.code,
    userStore.getExternalUserId
  );

  if (res.data) {
    sortedFeed.value = sortFeedContent(res.data);
  }
}

async function getHighlightArticles() {
  const res = await fetchPromotionArticles(clientStore.code);

  if (res.data) {
    highlightArticles.value = res.data;
  }
}

function searchArticles(): any[] {
  if (!searchTerm.value || searchTerm.value.length < 3) return [];

  const searchTokens = searchTerm.value
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  searchedFeed.value = sortedFeed.value.filter((article) => {
    const haystack = [
      article.title,
      article.goalTitle,
      article.shortDescription,
    ]
      .filter(Boolean)
      .map((s) => s.toLowerCase())
      .join(" ");

    return searchTokens.every((token) => haystack.includes(token));
  });
}

function closeSearch() {
  getArticleFeed();
  searchTerm.value = null;
  searchedFeed.value = null;
}
</script>
